
import { defineComponent, reactive } from "vue";
import { getImagesByDateIncremental, getImagesDates } from "@/service/image.service";
import StationImageComponent from "./StationImage.component.vue";
import { Image } from "@/types/Image.type";

function safeToISOString(v: unknown): string | undefined {
  if (!v) return undefined;
  if (v instanceof Date) {
    return isNaN(v.getTime()) ? undefined : v.toISOString();
  }
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  }
  if (typeof v === "object") {
    const anyV: any = v;
    if (anyV && typeof anyV.toISOString === "function") {
      try {
        const iso = anyV.toISOString();
        if (typeof iso === "string") return iso;
      } catch (e) {
        console.error(e);
      }
    }
    try {
      const coerced = new Date(String(anyV));
      if (!isNaN(coerced.getTime())) return coerced.toISOString();
    } catch (e) {
      console.error(e);
    }
  }
  return undefined;
}

export default defineComponent({
  components: { StationImageComponent },
  props: {
    title: { type: String, required: true },
    stationId: { type: String, required: true },
    camera: { type: String, required: false },
  },
  data() {
    return {
      images: [] as Image[],
      hasMore: true,
      fromDate: "" as string | undefined,
      toDate: "" as string | undefined,
      showDatePickers: false,
      datesWithData: reactive<string[]>([]),
      dateKey: 0,
      cameraSelect: undefined as string | undefined,
      type: undefined as string | undefined,
      capture: false as boolean,
      lastDate: undefined as string | undefined,
      firstLoad: true,
      loadingMore: false,
      originFilter: "STATION" as string,
      TAKE: 10,
      currentFilter: {
        from: undefined as string | undefined,
        to: undefined as string | undefined,
        camera: undefined as string | undefined,
        type: undefined as string | undefined,
        origin: "STATION" as string,
      },
    };
  },
  methods: {
    onToggleDatePicker() {
      this.showDatePickers = !this.showDatePickers;
      if (this.showDatePickers) this.getMarkedDates();
    },
    close() {
      this.$emit("close");
    },
    async getMarkedDates() {
      try {
        const respAny: any = await getImagesDates(this.stationId);
        const payload = Array.isArray(respAny) ? respAny : respAny?.data ?? respAny;
        const dates = (payload || [])
          .map((d: any) => {
            const iso = safeToISOString(d.image_date ?? d.date ?? d);
            return iso ? new Date(iso).toDateString() : "";
          })
          .filter((s: string) => !!s);
        this.datesWithData.splice(0, this.datesWithData.length, ...dates);
        this.dateKey++;
      } catch (err) {
        console.error("Error fetching dates:", err);
      }
    },
    hasEstimatedData(day: Date) {
      return this.datesWithData.includes(day.toDateString());
    },

    mergeAndSortUnique(newImgs: Image[]) {
      const map = new Map<string | number, Image>();
      this.images.forEach((i) => map.set((i as any).id, i));
      newImgs.forEach((i) => map.set((i as any).id, i));
      const arr = Array.from(map.values());
      arr.sort((a: any, b: any) => {
        const aIso = safeToISOString((a as any).dateTime) ?? "";
        const bIso = safeToISOString((b as any).dateTime) ?? "";
        return bIso.localeCompare(aIso);
      });
      this.images = arr;
    },

    isoMinusOneMs(iso?: string | undefined): string | undefined {
      if (!iso) return undefined;
      const d = new Date(iso);
      d.setMilliseconds(d.getMilliseconds() - 1);
      return d.toISOString();
    },

    getEffectiveCamera() {
      return this.cameraSelect || (this as any).camera || undefined;
    },

    hasFiltersApplied() {
      const cameraEffective = this.getEffectiveCamera();
      return !!(this.fromDate || this.toDate || cameraEffective || this.type);
    },

    async searchImages() {
      this.currentFilter.from = this.fromDate || undefined;
      this.currentFilter.to = this.toDate || undefined;
      this.currentFilter.camera = this.getEffectiveCamera();
      this.currentFilter.type = this.type || undefined;
      // solo para massive capture
      this.currentFilter.origin = this.originFilter;

      this.images = [];
      this.lastDate = undefined;
      this.firstLoad = true;
      this.hasMore = true;
      //-
      try {
        const respAny: any = await getImagesByDateIncremental(
          this.stationId,
          this.currentFilter.from,
          false,
          this.currentFilter.camera,
          this.currentFilter.to,
          this.currentFilter.type,
          this.currentFilter.origin
        );

        const newImages: Image[] = Array.isArray(respAny) ? respAny : respAny?.data ?? [];

        newImages.sort((a: any, b: any) => {
          const aIso = safeToISOString((a as any).dateTime) ?? "";
          const bIso = safeToISOString((b as any).dateTime) ?? "";
          return bIso.localeCompare(aIso);
        });

        this.images = newImages.slice();

        if (newImages.length > 0) {
          const serverLast = safeToISOString(newImages[newImages.length - 1].dateTime);
          this.lastDate = serverLast;
        } else {
          this.lastDate = undefined;
        }

        this.hasMore = newImages.length === this.TAKE;
      } catch (err) {
        console.error("Error en searchImages:", err);
        this.hasMore = false;
      } finally {
        this.firstLoad = false;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;

      try {
        const take = this.TAKE;
        const camera = this.getEffectiveCamera();
        const previousLast = this.lastDate;

        let respAny: any;

        if (this.hasFiltersApplied()) {
          const originalFrom = this.currentFilter.from;
          const originalTo = this.currentFilter.to;

          if (originalFrom && originalTo) {
            const nextTo = this.isoMinusOneMs(this.lastDate) || originalTo;

            respAny = await getImagesByDateIncremental(
              this.stationId,
              originalFrom,
              false,
              camera,
              nextTo,
              this.currentFilter.type,
              this.currentFilter.origin
            );
          } else {
            const fromCursor = this.isoMinusOneMs(this.lastDate);

            respAny = await getImagesByDateIncremental(
              this.stationId,
              fromCursor,
              true,
              camera,
              this.currentFilter.to,
              this.currentFilter.type
            );
          }
        } else {
          const fromCursor = this.isoMinusOneMs(this.lastDate);

          respAny = await getImagesByDateIncremental(
            this.stationId,
            fromCursor,
            true,
            camera,
            undefined,
            this.type
          );
        }

        const newImages: Image[] = Array.isArray(respAny) ? respAny : respAny?.data ?? [];

        newImages.sort((a: any, b: any) => {
          const aIso = safeToISOString((a as any).dateTime) ?? "";
          const bIso = safeToISOString((b as any).dateTime) ?? "";
          return bIso.localeCompare(aIso);
        });

        if (newImages.length === 0) {
          this.hasMore = false;
          return;
        }

        const serverLastDate = safeToISOString(newImages[newImages.length - 1].dateTime);

        this.mergeAndSortUnique(newImages);

        if (serverLastDate && serverLastDate === previousLast) {
          this.hasMore = false;
          this.lastDate = serverLastDate;
          return;
        }

        if (serverLastDate) this.lastDate = serverLastDate;

        this.hasMore = newImages.length === take;
      } catch (err) {
        console.error("Error en loadMore:", err);
      } finally {
        this.loadingMore = false;
        this.firstLoad = false;
      }
    },
  },

  mounted() {
    this.getMarkedDates();
    this.searchImages();
  },
});
